/**
 * EventDraftSummary — Inline draft draw preview for an event under My Rodeos.
 *
 * Renders inside `EventSection`'s expanded body so a delegate can see the
 * saved draft (or published) placement without leaving My Rodeos. Mirrors
 * the per-performance grouping logic of `RodeoDrawManagement` but in a
 * compact, read-only form: per-performance dropdowns that expand to show
 * each placed contestant's draw position, weight, and team partner.
 *
 * Gating
 *   - Backend `/draw-ops/event/:eventId/draft` is admin-only. Non-admin
 *     delegates still see the panel, but with a "requires admin" notice
 *     and a deep link to the full draw screen instead of an attempted
 *     fetch (which would 403). The deep link uses `useNavigate` with
 *     `location.state`, the same handshake `DelegateDrawsPage` already
 *     reads at mount.
 *
 * No retries on the draft fetch — the "no draft yet" path is a 404, and
 * React Query's default 3-retry policy would add ~7s of stall on every
 * un-drawn event. `httpClient` doesn't retry 4xx itself, but the React
 * Query layer does, so we opt out explicitly here.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ChevronRight,
  ExternalLink,
  Shuffle,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { httpClient } from '@/api/http-client';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import {
  drawOpsPlacementKeys,
  useCapacityConfig,
} from '@/api/extensions/useDrawOps';

// Mirror of the enriched draft row shape the backend returns
// (`buildDraftExecution` in draft-persistence.service.ts).
interface DraftEntry {
  id: string;
  registration_id: string;
  performance_id: string | null;
  draw_position: number;
  priority_position: number | null;
  is_placed: boolean;
  unplaced_reason: string | null;
  display_name?: string;
  non_member?: boolean;
  buddy_group_id?: string | null;
  buddy_group_number?: number | null;
  is_draw_out?: boolean;
  preference_outcome?:
    | 'first'
    | 'second'
    | 'none'
    | 'draw_out'
    | 'unplaced'
    // Finals rodeo — preferences don't apply.
    | 'finals';
  is_team?: boolean;
  is_team_primary?: boolean;
  team_position?: string | null;
  team_partner_name?: string | null;
}

interface DraftExecution {
  execution_id: string;
  event_id: string;
  placement_status: 'none' | 'draft' | 'published' | 'finalized';
  entries: DraftEntry[];
  warnings?: string[];
}

export interface EventDraftSummaryProps {
  eventId: string;
  eventName: string;
  rodeoId: string;
}

const UNDRAWN = '__undrawn__';
const DRAWOUT = '__drawout__';

export function EventDraftSummary({
  eventId,
  eventName,
  rodeoId,
}: EventDraftSummaryProps) {
  const navigate = useNavigate();
  // `isAdmin` is a selector method on the store; subscribe to `role` so
  // we re-render when the user's role changes (e.g. after impersonation).
  const role = useAuthStore((s) => s.role);
  const isAdmin =
    role?.isAdmin === true || role?.role_name === 'admin';

  const [perfsExpanded, setPerfsExpanded] = useState<Set<string>>(new Set());

  // Open the standalone draw screen with the event/rodeo pre-selected.
  // `DelegateDrawsPage` reads exactly these keys from `location.state` at
  // mount, so the deep link arrives directly on RodeoDrawManagement.
  const openDrawScreen = () => {
    navigate('/delegate/draws', {
      state: { eventId, eventName, rodeoId, drawKind: 'rodeo' },
    });
  };

  // Light perf list — already memo-cached by RodeoDrawManagement when it
  // opens the same event, so this is usually a cache hit. Admin-only.
  const { data: capacity } = useCapacityConfig(isAdmin ? eventId : '');

  // Draft fetch. retry:false so 404 (DRAFT_NOT_FOUND) lands instantly.
  // queryKey matches `useDraftDraw` so the cache is shared with the full
  // draw screen — opening the screen after viewing the summary doesn't
  // trigger a second request.
  const draftQuery = useQuery<DraftExecution>({
    queryKey: drawOpsPlacementKeys.draft(eventId),
    queryFn: () =>
      httpClient.get<DraftExecution>(`/draw-ops/event/${eventId}/draft`),
    enabled: !!eventId && isAdmin,
    retry: false,
  });

  // Group entries by performance the same way `RodeoDrawManagement` does:
  // - removed draw-outs → their own bucket
  // - unplaced / no perf → "not drawn" bucket
  // - otherwise → bucket by performance_id
  const byPerf = useMemo(() => {
    const groups = new Map<string, DraftEntry[]>();
    for (const e of draftQuery.data?.entries ?? []) {
      const removedDrawOut = e.preference_outcome === 'draw_out';
      const notInPerf = !e.is_placed || !e.performance_id;
      const key: string = removedDrawOut
        ? DRAWOUT
        : notInPerf
          ? UNDRAWN
          : (e.performance_id as string);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(e);
    }
    for (const list of groups.values()) {
      list.sort((a, b) => a.draw_position - b.draw_position);
    }
    return groups;
  }, [draftQuery.data?.entries]);

  const togglePerf = (id: string) => {
    setPerfsExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Non-admin delegates: no fetch, just a deep link.
  if (!isAdmin) {
    return (
      <DraftFrame>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Shuffle className="h-3.5 w-3.5" />
            Draft details require an admin role.
          </div>
          <Button size="sm" variant="outline" onClick={openDrawScreen}>
            Open draw screen
            <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </DraftFrame>
    );
  }

  if (draftQuery.isPending) {
    return (
      <DraftFrame>
        <p className="text-xs text-muted-foreground italic">
          Loading draft draw…
        </p>
      </DraftFrame>
    );
  }

  // Distinguish "no draft yet" (404 / DRAFT_NOT_FOUND) from a real failure.
  // The http-client tags errors with `status` + `code`, so we read both.
  const err = draftQuery.error as
    | (Error & { status?: number; code?: string })
    | undefined;
  const isNoDraft =
    draftQuery.isError &&
    (err?.code === 'DRAFT_NOT_FOUND' || err?.status === 404);

  if (isNoDraft) {
    return (
      <DraftFrame>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Shuffle className="h-3.5 w-3.5" />
            No draft draw yet — open the draw screen to generate one.
          </div>
          <Button size="sm" variant="outline" onClick={openDrawScreen}>
            Open draw screen
            <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </DraftFrame>
    );
  }

  if (draftQuery.isError) {
    return (
      <DraftFrame>
        <div className="flex items-center gap-2 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5" />
          Failed to load draft: {err?.message ?? 'Unknown error'}
        </div>
      </DraftFrame>
    );
  }

  const draft = draftQuery.data!;
  const entries = draft.entries ?? [];
  const placementStatus = draft.placement_status ?? 'none';
  const perfs = capacity?.performances ?? [];
  // Finals rodeo: every contestant runs every performance the event's rounds
  // are tied to, so each section is labelled by its round and ONE entry owns
  // one row per performance — count distinct registrations, not rows.
  const isFinals = capacity?.is_finals === true;
  const entryCount = isFinals
    ? new Set(entries.map((e) => e.registration_id)).size
    : entries.length;
  const drawOuts = byPerf.get(DRAWOUT) ?? [];
  const undrawn = byPerf.get(UNDRAWN) ?? [];

  return (
    <DraftFrame>
      {/* Status header + open-screen button */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <Badge
            variant="default"
            className={
              placementStatus === 'finalized'
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white text-[10px]'
                : placementStatus === 'published'
                  ? 'bg-green-600 hover:bg-green-700 text-white text-[10px]'
                  : 'bg-yellow-500 hover:bg-yellow-600 text-white text-[10px]'
            }
          >
            {placementStatus === 'finalized'
              ? 'Finalized'
              : placementStatus === 'published'
                ? 'Locked'
                : 'Draft'}
          </Badge>
          <span className="text-muted-foreground">
            {entryCount} {entryCount === 1 ? 'entry' : 'entries'}
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={openDrawScreen}>
          Open draw screen
          <ExternalLink className="h-3 w-3 ml-1" />
        </Button>
      </div>

      {/* Per-performance dropdowns */}
      <div className="space-y-1">
        {perfs.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            No performances configured.
          </p>
        )}
        {perfs.map((p) => {
          const rows = byPerf.get(p.performance_id) ?? [];
          return (
            <PerfDropdown
              key={p.performance_id}
              title={
                isFinals && p.round_label
                  ? `${p.round_label} — ${p.performance_name}`
                  : p.performance_name
              }
              subtitle={`${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}${
                p.is_slack ? ' · slack' : ''
              }`}
              rows={rows}
              expanded={perfsExpanded.has(p.performance_id)}
              onToggle={() => togglePerf(p.performance_id)}
            />
          );
        })}
        {drawOuts.length > 0 && (
          <PerfDropdown
            title="Drawn out"
            subtitle={`${drawOuts.length} ${drawOuts.length === 1 ? 'contestant' : 'contestants'}`}
            rows={drawOuts}
            expanded={perfsExpanded.has(DRAWOUT)}
            onToggle={() => togglePerf(DRAWOUT)}
          />
        )}
        {undrawn.length > 0 && (
          <PerfDropdown
            title="Not drawn"
            subtitle={`${undrawn.length} ${undrawn.length === 1 ? 'contestant' : 'contestants'}`}
            rows={undrawn}
            expanded={perfsExpanded.has(UNDRAWN)}
            onToggle={() => togglePerf(UNDRAWN)}
          />
        )}
      </div>
    </DraftFrame>
  );
}

function DraftFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Draft Draw
      </h4>
      <div className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-2">
        {children}
      </div>
    </div>
  );
}

interface PerfDropdownProps {
  title: string;
  subtitle: string;
  rows: DraftEntry[];
  expanded: boolean;
  onToggle: () => void;
}

function PerfDropdown({
  title,
  subtitle,
  rows,
  expanded,
  onToggle,
}: PerfDropdownProps) {
  return (
    <div className="rounded-md border border-border/40 bg-background overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="flex items-center gap-2 px-2 py-1.5 cursor-pointer select-none hover:bg-muted/40 transition-colors text-xs"
      >
        <ChevronRight
          className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform duration-200 ${
            expanded ? 'rotate-90' : ''
          }`}
        />
        <span className="font-medium">{title}</span>
        <span className="text-muted-foreground">· {subtitle}</span>
      </div>
      {expanded && rows.length > 0 && (
        <ol className="divide-y divide-border/40 bg-muted/10">
          {rows.map((e) => (
            <li
              key={e.id}
              className="flex items-center gap-2 px-2 py-1 text-xs"
            >
              <span className="w-6 text-right text-muted-foreground tabular-nums shrink-0">
                {e.draw_position}.
              </span>
              <span className="flex-1 truncate font-medium">
                {e.display_name ?? '—'}
                {e.is_team && e.team_partner_name && (
                  <span className="text-muted-foreground font-normal">
                    {' & '}
                    {e.team_partner_name}
                  </span>
                )}
              </span>
              {e.non_member && (
                <Badge
                  variant="outline"
                  className="text-[9px] py-0 h-4 shrink-0"
                >
                  Non-member
                </Badge>
              )}
              {e.is_team && (
                <Badge
                  variant="outline"
                  className="text-[9px] py-0 h-4 shrink-0"
                >
                  <Users className="h-2 w-2 mr-0.5" />
                  Team
                </Badge>
              )}
              {!e.non_member && e.priority_position != null && (
                <span className="text-muted-foreground tabular-nums shrink-0">
                  w{e.priority_position}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      {expanded && rows.length === 0 && (
        <div className="px-3 py-2 text-xs text-muted-foreground italic">
          None.
        </div>
      )}
    </div>
  );
}

export default EventDraftSummary;
